import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Trash2, RefreshCw, Database, Users, FileSpreadsheet } from "lucide-react";

const CONFIRMATION_PHRASE = "RESET PRODUCTION DATA";

export default function DangerZone() {
  const [preview, setPreview] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  // Lighter "clear reconciliation only" flow
  const [reconPreview, setReconPreview] = useState(null);
  const [reconOpen, setReconOpen] = useState(false);
  const [reconPhrase, setReconPhrase] = useState("");
  const [reconRunning, setReconRunning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [a, b] = await Promise.all([
          api.get("/admin/factory-reset/preview"),
          api.get("/admin/clear-reconciliation/preview"),
        ]);
        if (cancelled) return;
        setPreview(a.data);
        setReconPreview(b.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const runReset = async () => {
    if (phrase !== CONFIRMATION_PHRASE) {
      toast.error(`You must type the phrase '${CONFIRMATION_PHRASE}' exactly to continue.`);
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post("/admin/factory-reset", { confirmation: CONFIRMATION_PHRASE });
      setResult(data);
      setConfirmOpen(false);
      setPhrase("");
      setReloadKey((k) => k + 1);
      toast.success(`Reset complete: ${data.wiped_total} docs wiped, ${data.import.created} employees imported.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const runClearRecon = async () => {
    if (reconPhrase !== "CLEAR RECONCILIATION") {
      toast.error("Type the phrase 'CLEAR RECONCILIATION' exactly.");
      return;
    }
    setReconRunning(true);
    try {
      const { data } = await api.post("/admin/clear-reconciliation", { confirmation: "CLEAR RECONCILIATION" });
      toast.success(`Cleared ${data.wipe_total} reconciliation rows.`);
      setReconOpen(false);
      setReconPhrase("");
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReconRunning(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!preview) return <div className="p-6 text-sm text-muted-foreground">Could not load.</div>;

  const collections = preview.collections_to_wipe || {};
  const nonEmpty = Object.entries(collections).filter(([, v]) => v > 0);

  return (
    <div className="space-y-4" data-testid="danger-zone">
      <Alert className="border-red-300 bg-red-50">
        <AlertTriangle className="w-4 h-4 text-red-700" />
        <AlertTitle className="text-red-900">Factory reset — irreversible</AlertTitle>
        <AlertDescription className="text-red-900">
          Wipes all operational data (employees, attendance, leave, sick, pension, audit log, notifications, sessions, login attempts, etc.),
          removes every user except the 4 seed accounts, and re-imports the Employee Register .xls shipped with this deployment.
          Settings (company, SMTP, policies, public holidays, shift presets) are preserved.
        </AlertDescription>
      </Alert>

      <Card className="border-border shadow-none">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-semibold flex items-center gap-2"><Database className="w-4 h-4" /> What will be wiped</div>
              <p className="text-xs text-muted-foreground mt-1">{preview.wipe_total.toLocaleString()} documents across {nonEmpty.length} collection(s).</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)} data-testid="dz-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          {nonEmpty.length === 0 ? (
            <div className="text-sm text-muted-foreground">All operational collections are already empty.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {nonEmpty.map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-2 px-2.5 py-1.5 rounded border border-border">
                  <span className="font-mono text-muted-foreground truncate" title={k}>{k}</span>
                  <span className="font-semibold tabular-nums">{v.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <CardContent className="p-5 space-y-3">
          <div className="font-display font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> Users after reset</div>
          <div className="text-xs text-muted-foreground">
            {preview.users_to_remove} user(s) will be removed. {preview.users_kept} seeded user(s) will remain (or be re-created):
          </div>
          <ul className="space-y-1.5">
            {(preview.seed_users || []).map((u) => (
              <li key={u.email} className="text-sm flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">✓</span>
                <span className="font-medium">{u.email}</span>
                <span className="text-xs text-muted-foreground">({u.role})</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <CardContent className="p-5 space-y-3">
          <div className="font-display font-semibold flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Employee import</div>
          <div className="text-xs">
            {preview.register_file_present ? (
              <span className="text-emerald-700">Register file found. {preview.employees_to_import} employee row(s) will be imported.</span>
            ) : (
              <span className="text-red-700">⚠ Register file NOT found on this deployment. Reset will fail import — re-deploy with backend/data/employee_register.xls present.</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-red-300 bg-red-50/40 shadow-none">
        <CardContent className="p-5 space-y-3">
          <div className="font-display font-semibold text-red-900 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Run factory reset</div>
          <p className="text-xs text-red-900/80">
            You will be signed out immediately after the reset completes (all sessions are wiped). You will need to sign back in.
          </p>
          <Button
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            disabled={!preview.register_file_present}
            data-testid="dz-open-confirm"
          >
            <Trash2 className="w-4 h-4 mr-1.5" /> Reset & re-import
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Alert data-testid="dz-result">
          <AlertTitle>Reset complete</AlertTitle>
          <AlertDescription>
            <div className="mt-2 space-y-1 text-xs font-mono">
              <div>Wiped: <b>{result.wiped_total.toLocaleString()}</b> documents</div>
              <div>Users removed: <b>{result.non_seed_users_removed}</b></div>
              <div>Seed users: created {result.seed_users.created}, updated {result.seed_users.updated}</div>
              <div>Employees imported: <b>{result.import.created}</b></div>
              <div>By manager: Ashleigh={result.import.by_label.Ashleigh}, Cheryl={result.import.by_label.Cheryl}, Noreen={result.import.by_label.Noreen}, Unassigned={result.import.by_label.Default}, Ceased={result.import.by_label.Ceased}</div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Dialog open={reconOpen} onOpenChange={(o) => { if (!reconRunning) setReconOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Clear reconciliation data
            </DialogTitle>
            <DialogDescription>
              {reconPreview?.wipe_total.toLocaleString()} rows across pension + insurance reconciliation collections will be permanently deleted.
              This does NOT touch employees, attendance, users, or settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Confirmation phrase:</div>
            <code className="block px-3 py-2 bg-muted rounded text-sm font-mono">CLEAR RECONCILIATION</code>
            <Input
              value={reconPhrase}
              onChange={(e) => setReconPhrase(e.target.value)}
              placeholder="Type the phrase here"
              autoComplete="off"
              data-testid="dz-recon-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconOpen(false)} disabled={reconRunning}>Cancel</Button>
            <Button onClick={runClearRecon} disabled={reconRunning || reconPhrase !== "CLEAR RECONCILIATION"} data-testid="dz-recon-confirm-run">
              {reconRunning ? "Clearing…" : "Clear reconciliation rows"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!running) setConfirmOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> This cannot be undone
            </DialogTitle>
            <DialogDescription>
              Type the phrase below <b>exactly</b> to confirm. {preview.wipe_total.toLocaleString()} documents will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Confirmation phrase:</div>
            <code className="block px-3 py-2 bg-muted rounded text-sm font-mono">{CONFIRMATION_PHRASE}</code>
            <Input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder="Type the phrase here"
              autoComplete="off"
              data-testid="dz-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={running} data-testid="dz-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={runReset}
              disabled={running || phrase !== CONFIRMATION_PHRASE}
              data-testid="dz-confirm-run"
            >
              {running ? "Resetting…" : "I understand — reset now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
